import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OpsSettingsType } from '@models/ops-settings';
import { VesselService } from '@service/vessel.service';
import { Vessel } from 'icreate/common/models/vessel';
import { SystemSettingComponent } from './system-setting/system-setting.component';
import { from, Observable, Subject, take, takeUntil } from 'rxjs';
import { OutputSettingsComponent } from './output-settings/output-settings.component';
import { VesselSocketService } from '@service/vessel-socket.service';

@Component({
    selector: 'app-ops-settings',
    templateUrl: './ops-settings.component.html',
    styleUrls: ['./ops-settings.component.css'],
    standalone: false,
})
export class OpsSettingsComponent implements OnInit {
    readonly OPS_SETTING_TABS = Object.values(OpsSettingsType);

    readonly OPS_SYSTEM = OpsSettingsType.OpsSystem;
    readonly USER_ACCOUNT = OpsSettingsType.UserAccount;
    readonly DUTY_ENGINEER = OpsSettingsType.DutyEngineer;
    readonly SYSTEM_COLOR = OpsSettingsType.SystemColor;
    readonly OUTPUT_SETTINGS = OpsSettingsType.OutputSettings;
    readonly CONSOLE_SETTINGS = OpsSettingsType.ConsoleSettings;
    readonly GRAPHS = OpsSettingsType.Graphs;
    readonly EAP_SETTINGS = OpsSettingsType.EAPSettings;
    readonly EXT_COMM_SETTINGS = OpsSettingsType.ExtCommSettings;

    @ViewChild(SystemSettingComponent) systemSettingComponent!: SystemSettingComponent;
    @ViewChild(OutputSettingsComponent) outputSettingsComponent!: OutputSettingsComponent;

    vessel: Vessel;
    selectedTab: string = this.OPS_SETTING_TABS[0];
    isLoading: boolean = false;
    userCanEdit: boolean = true;
    destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        private vesselService: VesselService,
        private router: Router,
        private route: ActivatedRoute,
        private vesselSocketService: VesselSocketService,
        private cdr: ChangeDetectorRef
    ) {}

    async ngOnInit() {
        this.isLoading = true;
        const params = this.route.snapshot.params;
        const vesselId = params['vesselId'];
        const currentVessel: any = await this.vesselService.getVessel(vesselId);
        if (!currentVessel) {
            this.router.navigateByUrl('home');
            return;
        }
        this.vesselService.setActiveVessel(currentVessel);
        this.vessel = currentVessel;

        this.route.queryParamMap.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(queryParamMap => {
            const queryTab = queryParamMap.get('selectedTab');
            const stateTab = history.state?.selectedTab;

            if (queryTab || stateTab) {
                this.selectedTab = queryTab || stateTab;
            }
        });

        this.vesselService.accessUpdated$.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(canEdit => {
            this.userCanEdit = canEdit;
        });

        this.vesselSocketService
            .vesselUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(async (updatedVessel: Vessel) => {
                if (this.vessel.id === updatedVessel.id) {
                    this.vessel = updatedVessel;
                    this.vesselService.setActiveVessel(this.vessel);
                    this.cdr.detectChanges();
                }
            });

        this.isLoading = false;
    }

    ngOnDestroy(): void {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    async tabClick(tab: OpsSettingsType) {
        if (this.selectedTab == OpsSettingsType.OpsSystem) {
            const canLeave = await this.systemSettingComponent.canExit();
            if (!canLeave) return;
        } else if (this.selectedTab == OpsSettingsType.OutputSettings) {
            const shouldLeave = await this.outputSettingsComponent.shouldLeaveConfirmation();
            if (!shouldLeave) return;
        }
        this.selectedTab = tab;
    }

    async backButtonClick() {
        if (this.selectedTab == OpsSettingsType.OpsSystem) {
            const canLeave = await this.systemSettingComponent.canExit();
            if (!canLeave) return;
        }
        this.router.navigateByUrl(`home/${this.vessel.shipyardId}/${this.vessel.id}`);
    }

    static CanDeactivate(component: OpsSettingsComponent): boolean | Observable<boolean> {
        if (component.selectedTab == OpsSettingsType.OutputSettings) {
            return from(component.outputSettingsComponent.shouldLeaveConfirmation()).pipe(take(1));
        }
        return true;
    }
}
